// AppShell - responsive app chrome for Timesheet (ported pattern from AIMS/Hire).
// Replaces the old empty <Header>, blue <Footer> and slide-in <UserNavBar>.
// One sticky top bar: 1MS logo (-> home) + dark toggle + a single menu holding
// all navigation (Timesheet, Admin) and account actions (Reset Password, Log out).
//
// Rendered by App.js around the routes. The menu only appears when logged in
// (a username is in localStorage); logged-out shows just the logo + dark toggle,
// and the page body (TimesheetView) surfaces its own Login button.

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { baseUrl } from '../shared';
import { useColorMode } from '../theme/ThemeContext';
import { ResetPassword } from '../pages/resetPassword';
import logo from '../pages/logo.png';

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Tooltip from '@mui/material/Tooltip';

import MenuIcon from '@mui/icons-material/Menu';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import LightModeIcon from '@mui/icons-material/LightMode';
import HomeIcon from '@mui/icons-material/Home';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LockResetIcon from '@mui/icons-material/LockReset';
import LogoutIcon from '@mui/icons-material/Logout';

const isLoggedIn = () => !!localStorage.getItem('username');
const canAdmin = () => localStorage.getItem('canAdmin') === '1';
const displayName = () => (localStorage.getItem('name') || '').replace(/"/g, '') || 'Account';

function BrandLogo({ size = 34 }) {
  return (
    <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
      <Box component="img" src={logo} alt="1 Maintenance & Supply" sx={{ height: size, width: 'auto', display: 'block' }} />
    </Box>
  );
}

export default function AppShell({ children }) {
  const { mode, toggle } = useColorMode();
  const navigate = useNavigate();

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);

  const loggedIn = isLoggedIn();
  const admin = canAdmin();

  const closeMenu = () => setMenuAnchor(null);

  const goto = (to) => {
    closeMenu();
    navigate(to);
  };

  const doLogout = () => {
    fetch(baseUrl + 'user/logout', {
      method: 'POST',
      headers: {
        username: localStorage.getItem('username'),
        bToken: localStorage.getItem('bToken'),
      },
    })
      .then((r) => r.json())
      .catch(() => {})
      .finally(() => {
        localStorage.clear();
        window.location.href = '/';
      });
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <AppBar
        component="div"
        position="sticky"
        elevation={0}
        sx={{ background: 'var(--rail)', borderBottom: '1px solid rgba(255,255,255,.08)' }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <BrandLogo size={34} />

          <Box sx={{ flex: 1 }} />

          <Tooltip title={mode === 'dark' ? 'Light mode' : 'Dark mode'}>
            <IconButton onClick={toggle} sx={{ color: 'var(--on-rail)' }} aria-label="toggle colour mode">
              {mode === 'dark' ? <LightModeIcon /> : <BedtimeIcon />}
            </IconButton>
          </Tooltip>

          {loggedIn && (
            <>
              <Tooltip title="Menu">
                <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ color: 'var(--on-rail)' }} aria-label="menu">
                  <MenuIcon />
                </IconButton>
              </Tooltip>
              <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="caption" sx={{ color: 'var(--muted)' }}>
                    Signed in as
                  </Typography>
                  <Typography sx={{ fontWeight: 700 }}>{displayName()}</Typography>
                </Box>
                <Divider />
                <MenuItem onClick={() => goto('/')}>
                  <ListItemIcon>
                    <HomeIcon fontSize="small" />
                  </ListItemIcon>
                  Timesheet
                </MenuItem>
                {admin && (
                  <MenuItem onClick={() => goto('/admin')}>
                    <ListItemIcon>
                      <AdminPanelSettingsIcon fontSize="small" />
                    </ListItemIcon>
                    Admin
                  </MenuItem>
                )}
                <Divider />
                <MenuItem
                  onClick={() => {
                    closeMenu();
                    setResetOpen(true);
                  }}
                >
                  <ListItemIcon>
                    <LockResetIcon fontSize="small" />
                  </ListItemIcon>
                  Reset Password
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    closeMenu();
                    setLogoutOpen(true);
                  }}
                >
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  Log out
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Page content */}
      <Box component="main" sx={{ flex: 1, width: '100%', maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
        {children}
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          textAlign: 'center',
          py: 1.5,
          borderTop: '1px solid var(--border)',
          color: 'var(--muted)',
          fontSize: 12,
          background: 'var(--surface)',
        }}
      >
        Copyright © 2026 1 Maintenance &amp; Supply
      </Box>

      {/* Reset Password modal */}
      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Reset Password</DialogTitle>
        <DialogContent>
          <ResetPassword />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logout confirm */}
      <Dialog open={logoutOpen} onClose={() => setLogoutOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Log out?</DialogTitle>
        <DialogContent>
          <DialogContentText>You will need to sign in again to access your timesheets.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={doLogout} variant="contained">
            Log out
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
